import React, { useCallback, useEffect, useState } from 'react';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { Alert, AlertTitle, Box, Button, TextField } from '@mui/material';
import Loader from 'components/common/Loader';
import notify from 'lib/notify';
import { sendApiRequest } from 'lib/api/public';
import Uploader from 'components/common/Uploader';
import { renderField, validateUrl } from 'components/utils';
import CustomFieldsForm from 'components/common/CustomFieldsForm';
import SocialMediaLinksManager from 'components/common/SocialMediaLinksManager';
import { urlSchema } from 'components/common/utils';
import MassImportLink from 'components/common/MassImportLink';
import { useForm } from 'react-hook-form';
import { FormTextField, FormCustom, scrollToFirstFormErrorOnInvalid } from 'components/common/form';
import LocationFormField from 'components/common/LocationFormField';
import Modal from 'components/common/Modal';
import StatusSelector from 'components/common/StatusSelector';
import { Client, ClientFormData } from 'components/client/types';
import Group from 'components/common/Group';
import {
  useAutoSave,
  useEntitySimpleFieldAutosave,
  AutoSaveStatusIndicator,
  normalizeAttachmentsForAutosave,
} from '../../hooks/autoSave';
import { useRouter } from 'next/router';
import { boxStyle } from 'components/job/JobForm';
import StackTabs from 'components/common/StackTabs';
import RequireRoles from 'components/common/requireRole';
import { normalizeCustomFieldValues } from 'components/common/customFields/customFields';
import type { CustomFieldValues } from 'components/common/customFields/types';

const schema = yup
  .object({
    name: yup.string().required('Name is required'),
    logo: yup.string(),
    statusId: yup.string().required('Status is required'),
    notes: yup.string(),
    address: yup.object({
      street: yup.string(),
      city: yup.string(),
      state: yup.string(),
    }),
    websiteUrl: urlSchema,
    jobBoardUrl: urlSchema,
    linkedinUrl: urlSchema,
    crunchbaseUrl: urlSchema,
    companyEmailAddress: yup.string().email('Please enter a valid email address'),
    invoiceEmailAddress: yup.string().email('Please enter a valid email address'),
    contractEmailAddress: yup.string().email('Please enter a valid email address'),
    socialMediaLinks: yup.array().of(yup.string()),
    attachments: yup.array().of(
      yup.object({
        name: yup.string(),
        url: yup.string(),
        date: yup.date(),
      }),
    ),
    customFieldsValues: yup.object(),
  })
  .required();

const getAutosaveFormSnapshot = (
  values: { attachments?: ClientFormData['attachments'] } & Record<string, unknown>,
  customFields: CustomFieldValues,
) => ({
  ...values,
  attachments: normalizeAttachmentsForAutosave(values.attachments),
  customFieldsValues: customFields,
});

const getAutosaveValidationFields = (changedKeys: string[]) => {
  const fields: string[] = [];

  for (const key of changedKeys) {
    if (
      key === 'name' ||
      key === 'statusId' ||
      key === 'websiteUrl' ||
      key === 'jobBoardUrl' ||
      key === 'linkedinUrl' ||
      key === 'crunchbaseUrl' ||
      key === 'companyEmailAddress' ||
      key === 'invoiceEmailAddress' ||
      key === 'contractEmailAddress'
    ) {
      fields.push(key);
    }
  }

  return fields;
};

type Props = {
  onClose: () => void;
  onSuccess: (response?: Client) => void;
  open: boolean;
  object?: Client;
};

const ClientForm = ({ open, onClose, onSuccess, object }: Props) => {
  const router = useRouter();
  const query = router.query;

  const clientO = object || ({} as Client);
  const isUpdate = Boolean(clientO._id);

  const [clientUrl, setClientUrl] = useState('');
  const [tabValue, setTabValue] = useState(0);

  const [createFromUrlError, setCreateFromUrlError] = useState('');
  const [extracting, setExtracting] = useState(false);
  const [isExtracted, setIsExtracted] = useState(false);

  const [customFieldsValues, setCustomFieldsValues] = useState<CustomFieldValues>(
    normalizeCustomFieldValues(clientO.customFieldsValues),
  );

  const [saving, setSaving] = useState(false);

  const [contentHash, setContentHash] = useState(Math.random().toString());
  const [statusHash, setStatusHash] = useState(Math.random().toString());
  const [attachmentsHash, setAttachmentsHash] = useState(Math.random().toString());
  const [addressHash, setAddressHash] = useState(Math.random().toString());
  const [logoHash, setLogoHash] = useState(Math.random().toString());

  const { control, handleSubmit, setValue, watch, getValues, trigger } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      name: clientO.name,
      logo: clientO.logo,
      statusId: clientO.statusId || '',
      notes: clientO.notes,
      address: clientO.address,
      websiteUrl: clientO.websiteUrl,
      jobBoardUrl: clientO.jobBoardUrl,
      linkedinUrl: clientO.linkedinUrl,
      crunchbaseUrl: clientO.crunchbaseUrl,
      companyEmailAddress: clientO.companyEmailAddress,
      invoiceEmailAddress: clientO.invoiceEmailAddress,
      contractEmailAddress: clientO.contractEmailAddress,
      socialMediaLinks: clientO.socialMediaLinks,
      attachments: clientO.attachments,
      customFieldsValues: clientO.customFieldsValues,
    },
  });

  useEffect(() => {
    if (query.formTab) {
      setTabValue(Number(query.formTab));
    }
  }, [query.formTab]);

  // Watch all form values for auto-save
  const watchedValues = watch();

  const autoSave = useAutoSave({
    enabled: !isUpdate && tabValue === 1,
    type: 'client',
    data: {
      ...watchedValues,
      customFieldsValues,
    },
    onGetDraft: (draft: yup.InferType<typeof schema>) => {
      if (!isUpdate && draft) {
        setValue('name', draft.name);
        setValue('logo', draft.logo);
        setValue('statusId', draft.statusId);
        setValue('notes', draft.notes);
        setValue('address', draft.address);
        setValue('jobBoardUrl', draft.jobBoardUrl);
        setValue('websiteUrl', draft.websiteUrl);
        setValue('linkedinUrl', draft.linkedinUrl);
        setValue('crunchbaseUrl', draft.crunchbaseUrl);
        setValue('companyEmailAddress', draft.companyEmailAddress);
        setValue('invoiceEmailAddress', draft.invoiceEmailAddress);
        setValue('contractEmailAddress', draft.contractEmailAddress);
        setValue('socialMediaLinks', draft.socialMediaLinks);
        setValue('attachments', draft.attachments);

        setCustomFieldsValues(normalizeCustomFieldValues(draft.customFieldsValues));

        setStatusHash(Math.random().toString());
        setAttachmentsHash(Math.random().toString());
        setAddressHash(Math.random().toString());
        setLogoHash(Math.random().toString());
      }
    },
  });

  const formValues = watch();
  const serializedValues = JSON.stringify(getAutosaveFormSnapshot(formValues, customFieldsValues));

  const seedSnapshot = useCallback(
    () => JSON.stringify(getAutosaveFormSnapshot(getValues(), customFieldsValues)),
    [getValues, customFieldsValues],
  );

  const getValidationFields = useCallback((changedKeys: string[]) => getAutosaveValidationFields(changedKeys), []);

  const updateAutosave = useEntitySimpleFieldAutosave({
    enabled: isUpdate,
    isReady: isUpdate,
    snapshot: serializedValues,
    seedSnapshot,
    isBlocked: () => saving,
    getValidationFields,
    trigger,
    updatePath: '/clients/update-simple-field',
    idField: 'clientId',
    entityId: clientO._id,
  });

  const onSave = async (data: yup.InferType<typeof schema>) => {
    setSaving(true);

    try {
      const response = (await sendApiRequest({
        path: '/clients/create',
        method: 'POST',
        data: {
          ...data,
          customFieldsValues,
        },
      })) as Client;

      let successMessage = 'Client added successfully.';

      const { companyEmailAddress } = data;

      if (companyEmailAddress) {
        successMessage = `${successMessage} Please verify the email address.`;
      }

      notify(successMessage);

      onSuccess(response);

      void autoSave.deleteDraft('client');

      setSaving(false);
    } catch (error) {
      notify(error);
      setSaving(false);
    }
  };

  const onFetchClientDetails = async (url: string) => {
    if (!validateUrl(url)) {
      setCreateFromUrlError('Invalid URL');
      return;
    }

    setExtracting(true);

    try {
      const response = (await sendApiRequest({
        path: '/clients/extract-details-from-url',
        method: 'POST',
        data: { url },
      })) as {
        name: string;
        logo: string;
        description: string;
        address: { street: string; city: string; state: string };
        websiteUrl: string;
        jobBoardUrl: string;
        crunchbaseUrl: string;
        linkedinUrl: string;
        socialMediaLinks: string[];
        companyEmailAddress: string;
      };

      if (response && Object.keys(response).length > 0) {
        setIsExtracted(true);

        if (response.name) {
          setValue('name', response.name);
        }

        if (response.logo) {
          setValue('logo', response.logo);
        }

        if (response.description) {
          setValue('notes', response.description);
        }

        if (response.address) {
          setValue('address', {
            street: response.address.street || '',
            city: response.address.city || '',
            state: response.address.state || '',
          });
        }

        if (response.websiteUrl) {
          setValue('websiteUrl', response.websiteUrl);
        }

        if (response.jobBoardUrl) {
          setValue('jobBoardUrl', response.jobBoardUrl);
        }

        if (response.crunchbaseUrl) {
          setValue('crunchbaseUrl', response.crunchbaseUrl);
        }

        if (response.linkedinUrl) {
          setValue('linkedinUrl', response.linkedinUrl);
        }

        if (response.socialMediaLinks) {
          const smLinks: string[] = [];

          for (const link of response.socialMediaLinks) {
            if (link.includes('linkedin.com')) {
              setValue('linkedinUrl', link);
            } else {
              smLinks.push(link);
            }

            setValue('socialMediaLinks', smLinks);
          }
        }

        setContentHash(Math.random().toString());
      } else {
        notify('No details found');
      }
    } catch (error) {
      notify(error);
    } finally {
      setExtracting(false);
    }
  };

  const renderClientNameToFetchDetails = () => {
    if (clientO._id || isExtracted) {
      return null;
    }

    return (
      <Box sx={boxStyle}>
        <MassImportLink type="Clients" />

        <br />

        {extracting && <Loader />}

        <Alert severity="warning" sx={{ mb: 4 }}>
          <AlertTitle>Create from URL</AlertTitle>
          Paste URL to automatically populate client details.
        </Alert>

        <TextField
          size="small"
          label="Client URL"
          fullWidth
          placeholder="https://www.linkedin.com/company/github"
          slotProps={{ htmlInput: { 'data-testid': '@client-form-url' } }}
          onChange={e => {
            setClientUrl(e.target.value);
            setCreateFromUrlError('');
          }}
          error={!!createFromUrlError}
          helperText={createFromUrlError}
          sx={{ backgroundColor: 'background.paper', color: 'text.primary', marginBottom: 2 }}
          autoFocus
        />

        <Button
          variant="contained"
          color="primary"
          data-testid="@client-form-create-from-url"
          onClick={() => onFetchClientDetails(clientUrl)}
          disabled={extracting}
          sx={{ marginBottom: 2, float: 'right' }}
        >
          {extracting ? 'Extracting...' : 'Create from URL'}
        </Button>

        <div style={{ clear: 'both' }} />
      </Box>
    );
  };

  const renderForm = () => {
    const attachments = watch('attachments');
    const statusId = watch('statusId');
    const logo = watch('logo');

    const flexOptions = {
      label: 3,
      value: 9,
    };

    return (
      <Box sx={{ opacity: extracting ? 0.2 : 1 }} key={contentHash} data-testid="@client-form">
        <Group bg="white">
          {renderField({
            id: 'client_form_name',
            label: 'Name',
            required: true,
            flexOptions,
            renderControl: () => <FormTextField name="name" control={control} fullWidth testId="name" />,
          })}

          {renderField({
            id: 'client_form_status',
            label: 'Status',
            required: true,
            flexOptions,
            renderControl: () => {
              return (
                <FormCustom
                  key={statusHash}
                  name="statusId"
                  control={control}
                  renderControl={error => (
                    <StatusSelector
                      defaultValue={statusId}
                      testId="status"
                      onSelect={value => setValue('statusId', value)}
                      endpoint="/clients/statuses"
                      error={error}
                    />
                  )}
                />
              );
            },
          })}
        </Group>

        <Group>
          {renderField({
            id: 'client_form_logo',
            label: 'Logo',
            flexOptions,
            renderControl: () => {
              return (
                <>
                  <Uploader
                    key={logoHash}
                    label="Upload"
                    multiple={false}
                    defaultFiles={logo ? [{ name: logo, url: logo }] : []}
                    onChange={result => {
                      if (Array.isArray(result)) {
                        setValue('logo', result[0]?.url || '');
                        return;
                      }

                      setValue('logo', result?.url || '');
                    }}
                  />
                </>
              );
            },
          })}

          <LocationFormField
            key={addressHash}
            control={control}
            flexOptions={flexOptions}
            cityName="address.city"
            stateName="address.state"
          />

          {renderField({
            id: 'client_form_website',
            label: 'Website',
            flexOptions,
            renderControl: () => {
              return <FormTextField name="websiteUrl" control={control} fullWidth testId="website" />;
            },
          })}

          {renderField({
            id: 'client_form_job_board_url',
            label: 'Job Board URL',
            flexOptions,
            renderControl: () => {
              return <FormTextField name="jobBoardUrl" control={control} fullWidth testId="job-board-url" />;
            },
          })}

          {renderField({
            id: 'client_form_linkedin',
            label: 'LinkedIn',
            flexOptions,
            renderControl: () => {
              return <FormTextField name="linkedinUrl" control={control} fullWidth testId="linkedin-url" />;
            },
          })}

          {renderField({
            id: 'client_form_crunchbase',
            label: 'Crunchbase',
            flexOptions,
            renderControl: () => {
              return <FormTextField name="crunchbaseUrl" control={control} fullWidth />;
            },
          })}

          {renderField({
            id: 'client_form_other_social_media',
            label: 'Other Social Media',
            flexOptions,
            renderControl: () => (
              <SocialMediaLinksManager
                initialLinks={(watch('socialMediaLinks') || []).filter(
                  (link): link is string => typeof link === 'string',
                )}
                onChange={value => setValue('socialMediaLinks', value)}
              />
            ),
          })}

          {renderField({
            id: 'client_form_from_email_address',
            label: 'From email address',
            flexOptions,
            renderControl: () => {
              return <FormTextField name="companyEmailAddress" control={control} fullWidth />;
            },
          })}

          <RequireRoles roles={['superAdmin']}>
            {renderField({
              id: 'client_form_invoice_email_address',
              label: 'Contact for Invoices',
              flexOptions,
              renderControl: () => {
                return <FormTextField name="invoiceEmailAddress" control={control} fullWidth />;
              },
            })}

            {renderField({
              id: 'client_form_contract_email_address',
              label: 'Contact for Contracts',
              flexOptions,
              renderControl: () => {
                return <FormTextField name="contractEmailAddress" control={control} fullWidth />;
              },
            })}
          </RequireRoles>

          {renderField({
            id: 'client_form_attachments',
            label: 'Attachments',
            flexOptions,
            renderControl: () => {
              return (
                <Uploader
                  key={attachmentsHash}
                  label="Upload files"
                  defaultFiles={(attachments || []).map(attachment => ({
                    name: attachment.name,
                    url: attachment.url,
                    date: attachment.date,
                  }))}
                  multiple={true}
                  onChange={results => {
                    setValue('attachments', Array.isArray(results) ? results : [results]);
                  }}
                />
              );
            },
          })}
        </Group>

        <CustomFieldsForm
          groupId="client_form_custom_fields"
          type="client"
          initialValues={customFieldsValues}
          onChange={values => {
            setCustomFieldsValues(values);
          }}
        />
      </Box>
    );
  };

  const renderTabContent = () => {
    if (tabValue === 0) {
      return (
        <Box>
          {renderClientNameToFetchDetails()}

          {isExtracted && renderForm()}
        </Box>
      );
    }

    return renderForm();
  };

  const renderContent = () => {
    if (clientO._id) {
      return renderForm();
    }

    const tabs = [{ label: 'Create from URL', value: 0, testId: '@clients-create-from-url' }];

    if (!isExtracted) {
      tabs.push({ label: 'Create manually', value: 1, testId: '@clients-create-manually' });
    }

    return (
      <StackTabs
        noSpacing={true}
        tabs={tabs}
        tabValue={tabValue}
        setTabValue={setTabValue}
        renderTabContent={renderTabContent}
      />
    );
  };

  const renderUpdateAutoSave = () => (
    <AutoSaveStatusIndicator status={updateAutosave.status} lastAutoSave={updateAutosave.lastAutoSave} type="client" />
  );

  const renderCreateAutoSave = () => {
    if (tabValue === 1) {
      return (
        <AutoSaveStatusIndicator
          status={autoSave.status}
          lastAutoSave={autoSave.lastAutoSave}
          type="client"
          onDiscard={() => {
            setValue('name', '');
            setValue('logo', '');
            setValue('statusId', '');
            setValue('notes', '');
            setValue('address', {});
            setValue('websiteUrl', '');
            setValue('linkedinUrl', '');
            setValue('crunchbaseUrl', '');
            setValue('companyEmailAddress', '');
            setValue('invoiceEmailAddress', '');
            setValue('contractEmailAddress', '');
            setValue('socialMediaLinks', []);
            setValue('attachments', []);
            setCustomFieldsValues({});

            setStatusHash(Math.random().toString());
            setAttachmentsHash(Math.random().toString());
            setAddressHash(Math.random().toString());
            setLogoHash(Math.random().toString());

            autoSave.resetStatus();
          }}
        />
      );
    }
  };

  return (
    <Modal
      onClose={onClose}
      onSubmit={isUpdate ? undefined : handleSubmit(onSave, scrollToFirstFormErrorOnInvalid)}
      title={
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          {clientO.name ? clientO.name : 'Add'} Client {isUpdate ? renderUpdateAutoSave() : renderCreateAutoSave()}
        </Box>
      }
      open={open}
      fullScreen
      sx={{
        width: {
          xs: '100%',
          md: '902px',
        },
        left: 'inherit',
      }}
      renderActions={() => {
        if (isUpdate) {
          return null;
        }

        if (isExtracted || tabValue === 1) {
          return (
            <Button
              variant="contained"
              color="primary"
              type="submit"
              disabled={saving}
              data-testid="@client-form-submit"
            >
              Add
            </Button>
          );
        }
      }}
      renderContent={renderContent}
    />
  );
};

export default ClientForm;
